import { useState, useEffect } from 'react';
import { Box, Flex, Text, Grid, chakra } from '@chakra-ui/react';
import { RotateCcw, Save } from 'lucide-react';
import { BaseModal } from '@/components/ui/BaseModal';
import { Toast } from '@/components/ui/Toast/Toast';
import { NumberInput } from '@/components/ui/NumberInput';
import { ConfirmModal } from '@/components/ui/ConfirmModal/ConfirmModal';
import { LocationAutocomplete } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { ImagePicker, PublicToggle, StatusSelector, DepartmentsInput, ShiftsInput } from '@/pages/Organizations/components/shared';
import { DateRangeInputs } from './DateRangeInputs.tsx';
import { useEventFormBase } from '@/hooks/useEventFormBase';
import type { Organization } from '@/types/events';

interface EventModalProps {
  mode: 'create' | 'edit';
  visible: boolean;
  onClose: () => void;
  event?: Organization | null;
  userId: string;
  subscriptionActive?: boolean;
  onSuccess: () => void;
  onSubscriptionPrompt?: () => void;
}

export function EventModal({
  mode,
  visible,
  onClose,
  event,
  userId,
  subscriptionActive = true,
  onSuccess,
  onSubscriptionPrompt,
}: EventModalProps) {
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [pendingShiftRemoval, setPendingShiftRemoval] = useState<{ key: string; taken: number } | null>(null);

  const formData = useEventFormBase({
    mode,
    userId,
    event,
    subscriptionActive,
    onSuccess,
    onSubscriptionPrompt,
    onEndEventConfirm: async () => {
      setShowEndConfirm(true);
    },
  });

  const {
    name,
    location,
    description,
    startDate,
    endDate,
    slots,
    status,
    isPublic,
    eventImage,
    eventImagePreview,
    departments,
    shifts,
    shiftsLoading,
    newDepartment,
    isSaving,
    hasChanges,
    toast,
    setName,
    setLocation,
    setDescription,
    setStartDate,
    setEndDate,
    setSlots,
    setIsPublic,
    setNewDepartment,
    handleImageChange,
    handleRemoveExistingImage,
    handleAddDepartment,
    handleRemoveDepartment,
    handleAddShifts,
    handleRemoveShift,
    handleShiftCapacityChange,
    handleSave,
    handleEndEvent,
    executeEndEvent,
    resetForm,
    hideToast,
  } = formData;

  const isEdit = mode === 'edit';

  useEffect(() => {
    if (!visible) {
      resetForm();
    }
  }, [visible, resetForm]);

  const handleClose = () => {
    if (!isSaving) {
      resetForm();
      onClose();
    }
  };

  const handleUndoOrCancel = () => {
    if (!isSaving) {
      resetForm();
    }
  };

  // Removing a shift people are on also takes them off it when the event is saved.
  const requestRemoveShift = (key: string) => {
    const shift = shifts.find((s) => s.key === key);
    if (shift && shift.taken > 0) {
      setPendingShiftRemoval({ key, taken: shift.taken });
      return;
    }
    handleRemoveShift(key);
  };

  const handleEndConfirm = async () => {
    setShowEndConfirm(false);
    if (executeEndEvent) {
      await executeEndEvent();
    }
  };

  return (
    <>
      <BaseModal
        visible={visible}
        onClose={handleClose}
        title={isEdit ? 'Edit Event' : 'Create Event'}
        disabled={isSaving}
        maxWidth="lg"
        bodyClassName="p-4 md:p-6"
        footer={
          <Flex gap={{ base: '3', md: '4' }} w="full" justify={{ md: 'center' }}>
            {isEdit && event?.is_active && handleEndEvent && (
              <chakra.button
                onClick={handleEndEvent}
                disabled={isSaving}
                flex={{ base: '1', md: 'none' }}
                px={{ base: '6', md: '6' }}
                py="3"
                borderRadius="xl"
                fontWeight="semibold"
                transition="all 0.3s"
                cursor="pointer"
                border="none"
                outline="none"
                bg="volyx.lightGray"
                color="volyx.error"
                minH={{ md: '12' }}
                fontSize={{ md: 'md' }}
                _hover={{ bg: 'rgba(255,59,48,0.1)', transform: 'scale(1.01)' }}
              >
                End Event
              </chakra.button>
            )}
            {isEdit && (
              <chakra.button
                onClick={handleUndoOrCancel}
                disabled={isSaving || !hasChanges}
                flex={{ base: '1', md: 'none' }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                gap="2"
                minW="0"
                px={{ base: '3', sm: '6' }}
                py="3"
                borderRadius="xl"
                fontWeight="semibold"
                transition="all 0.3s"
                cursor="pointer"
                border="none"
                outline="none"
                bg="volyx.lightGray"
                color="volyx.text"
                minH={{ md: '12' }}
                fontSize={{ md: 'md' }}
                _hover={{ bg: 'volyx.mediumGray', transform: 'scale(1.01)' }}
                aria-label="Undo"
                title="Undo"
              >
                <Box as={RotateCcw} w="5" h="5" flexShrink={0} display={{ base: 'block', sm: 'none' }} aria-hidden />
                <Box as="span" display={{ base: 'none', sm: 'inline' }}>Undo</Box>
              </chakra.button>
            )}
            <chakra.button
              onClick={handleSave}
              disabled={isSaving || (isEdit && !hasChanges)}
              flex={{ base: '1', md: 'none' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap="2"
              minW="0"
              px={{ base: '6', md: '6' }}
              py="3"
              borderRadius="xl"
              fontWeight="semibold"
              transition="all 0.3s"
              cursor="pointer"
              border="none"
              outline="none"
              bg="volyx.action"
              color="white"
              boxShadow="0 4px 12px rgba(0,0,0,0.08)"
              minH={{ md: '12' }}
              fontSize={{ md: 'md' }}
              _hover={{ bg: 'rgba(0,122,255,0.9)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', transform: 'translateY(-2px) scale(1.02)' }}
              aria-label={isSaving ? 'Saving...' : isEdit ? 'Save' : 'Create Event'}
              title={isSaving ? 'Saving...' : isEdit ? 'Save' : 'Create Event'}
            >
              {isEdit && <Box as={Save} w="5" h="5" flexShrink={0} display={{ base: 'block', sm: 'none' }} aria-hidden />}
              {isEdit ? (
                <Box as="span" display={{ base: 'none', sm: 'inline' }}>{isSaving ? 'Saving...' : 'Save'}</Box>
              ) : (
                <Box as="span">{isSaving ? 'Saving...' : 'Create Event'}</Box>
              )}
            </chakra.button>
          </Flex>
        }
      >
        <Grid templateColumns={{ md: 'repeat(2, 1fr)' }} gap={{ md: '6' }}>
          <Box display="flex" flexDirection="column" gap={{ base: '0', md: '4' }}>
            <ImagePicker
              image={eventImage}
              imagePreview={isEdit ? eventImagePreview : undefined}
              onImageChange={handleImageChange}
              onRemoveImage={handleRemoveExistingImage}
              disabled={isSaving}
            />

            <Box mb={{ base: '4', md: '0' }}>
              <chakra.label
                htmlFor="event-name-input"
                display="block"
                mb="2"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="medium"
                color="volyx.text"
              >
                Event Name *
              </chakra.label>
              <chakra.input
                id="event-name-input"
                name="event-name"
                type="text"
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                placeholder="Enter event name"
                w="full"
                h="14"
                bg="volyx.lightGray"
                borderRadius="xl"
                border="none"
                px="4"
                fontSize="md"
                fontFamily="body"
                color="volyx.text"
                boxShadow="0 1px 3px rgba(0,0,0,0.08)"
                transition="all 0.2s"
                _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
                _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
                autoComplete="off"
                maxLength={35}
                disabled={isSaving}
              />
            </Box>

            <Box mb={{ base: '4', md: '0' }}>
              <chakra.label
                htmlFor="event-location-input"
                display="block"
                mb="2"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="medium"
                color="volyx.text"
              >
                Location *
              </chakra.label>
              <LocationAutocomplete
                id="event-location-input"
                name="event-location"
                value={location}
                onChange={setLocation}
                placeholder="Search for a city..."
                disabled={isSaving}
              />
            </Box>

            <Box mb={{ base: '4', md: '0' }}>
              <chakra.label
                htmlFor="event-description-input"
                display="block"
                mb="2"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="medium"
                color="volyx.text"
              >
                Description <Text as="span" color="volyx.textSecondary" fontSize={{ base: 'xs', md: 'sm' }}>(optional)</Text>
              </chakra.label>
              <chakra.textarea
                id="event-description-input"
                name="event-description"
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
                placeholder="Describe the event, key goals, responsibilities, or other useful info"
                w="full"
                minH="120px"
                bg="volyx.lightGray"
                borderRadius="xl"
                border="none"
                px="4"
                py="3"
                fontSize="md"
                fontFamily="body"
                color="volyx.text"
                boxShadow="0 1px 3px rgba(0,0,0,0.08)"
                transition="all 0.2s"
                _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
                _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
                resize="vertical"
                rows={6}
                maxLength={500}
                disabled={isSaving}
              />
              <Text display="block" mt="1" fontSize={{ base: 'xs', md: 'sm' }} color="volyx.textSecondary">
                {description.length}/500
              </Text>
            </Box>
          </Box>

          <Box display="flex" flexDirection="column" gap={{ base: '0', md: '4' }}>
            <PublicToggle isPublic={isPublic} onToggle={setIsPublic} disabled={isSaving} />

            <StatusSelector />

            {status === 'Scheduled' && (
              <DateRangeInputs
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                disabled={isSaving}
              />
            )}

            <NumberInput
              id="event-slots-input"
              name="event-slots"
              value={slots}
              onChange={setSlots}
              label="Number of Slots"
              hint="(max. 100)"
              min={1}
              max={100}
              required
              disabled={isSaving}
            />

            <DepartmentsInput
              departments={departments}
              newDepartment={newDepartment}
              onDepartmentChange={setNewDepartment}
              onAddDepartment={handleAddDepartment}
              onRemoveDepartment={handleRemoveDepartment}
              disabled={isSaving}
            />

            <ShiftsInput
              shifts={shifts}
              eventStartDate={startDate}
              eventEndDate={endDate}
              loading={shiftsLoading}
              onAddShifts={handleAddShifts}
              onRemoveShift={requestRemoveShift}
              onCapacityChange={handleShiftCapacityChange}
              disabled={isSaving}
            />
          </Box>
        </Grid>
      </BaseModal>

      <ConfirmModal
        isOpen={showEndConfirm}
        title="End Event"
        message="Are you sure you want to end this event? This action cannot be undone."
        confirmText="End Event"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleEndConfirm}
        onCancel={() => setShowEndConfirm(false)}
      />

      <ConfirmModal
        isOpen={pendingShiftRemoval !== null}
        title="Remove shift"
        message={
          pendingShiftRemoval
            ? `${pendingShiftRemoval.taken} ${pendingShiftRemoval.taken === 1 ? 'volunteer is' : 'volunteers are'} on this shift. They will be taken off it when you save.`
            : ''
        }
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
        onConfirm={() => {
          if (pendingShiftRemoval) handleRemoveShift(pendingShiftRemoval.key);
          setPendingShiftRemoval(null);
        }}
        onCancel={() => setPendingShiftRemoval(null)}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
    </>
  );
}
